import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Coin, ChatCircleText, Warning, Gift } from '@phosphor-icons/react';
import api from '../api/api';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Button } from './ui/button';

const kindIcon = (kind) => {
  if (kind === 'wallet_low') return <Coin size={16} className="text-amber-600" weight="fill" />;
  if (kind === 'lead_new') return <ChatCircleText size={16} className="text-primary" weight="fill" />;
  if (kind === 'new_message') return <ChatCircleText size={16} className="text-accent" weight="fill" />;
  if (kind === 'subscription_expiring') return <Warning size={16} className="text-red-600" weight="fill" />;
  return <Bell size={16} className="text-foreground/60" />;
};

const NotificationsBell = () => {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const fetchNotifs = useCallback(async () => {
    try {
      const r = await api.get('/api/notifications?limit=20');
      setItems(r.data.items || []);
      setUnread(r.data.unread_count || 0);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    fetchNotifs();
    const t = setInterval(fetchNotifs, 20000); // 20s poll
    const handler = () => fetchNotifs();
    window.addEventListener('vl:credit-refresh', handler);
    window.addEventListener('vl:notifications-refresh', handler);
    return () => {
      clearInterval(t);
      window.removeEventListener('vl:credit-refresh', handler);
      window.removeEventListener('vl:notifications-refresh', handler);
    };
  }, [fetchNotifs]);

  const clickItem = async (n) => {
    if (!n.is_read) {
      try { await api.post(`/api/notifications/${n.notification_id}/read`); } catch { /* silent */ }
    }
    setOpen(false);
    if (n.link) navigate(n.link);
    fetchNotifs();
  };

  const markAll = async () => {
    try { await api.post('/api/notifications/read-all'); } catch { /* silent */ }
    fetchNotifs();
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="relative p-2 rounded-full hover:bg-muted/60 transition-colors"
          data-testid="notifications-bell"
          aria-label="Notifiche"
        >
          <Bell size={20} />
          {unread > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center px-1"
              data-testid="notifications-badge"
            >
              {unread > 9 ? '9+' : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="notifications-popover">
        <div className="flex items-center justify-between p-3 border-b">
          <div className="font-semibold text-sm">Notifiche</div>
          {unread > 0 && (
            <Button size="sm" variant="ghost" onClick={markAll} className="h-7 text-xs" data-testid="mark-all-read-btn">
              Segna tutte come lette
            </Button>
          )}
        </div>
        <div className="max-h-80 overflow-y-auto">
          {items.length === 0 && (
            <div className="p-6 text-center text-sm text-foreground/60">Nessuna notifica</div>
          )}
          {items.map((n) => (
            <button
              key={n.notification_id}
              onClick={() => clickItem(n)}
              className={`w-full text-left p-3 border-b border-border/50 hover:bg-muted/40 transition-colors flex gap-2 ${n.is_read ? 'opacity-70' : ''}`}
              data-testid={`notif-item-${n.notification_id}`}
            >
              <div className="mt-0.5">{kindIcon(n.kind)}</div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium">{n.title}</div>
                <div className="text-xs text-foreground/70 line-clamp-2">{n.body}</div>
                <div className="text-[10px] text-foreground/50 mt-1">
                  {new Date(n.created_at).toLocaleString('it-IT')}
                </div>
              </div>
              {!n.is_read && <div className="w-2 h-2 rounded-full bg-primary mt-1.5" />}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationsBell;
